import { Badge } from '@design-system';
import {
  DurationSlideOutContent,
  CareHoursEndSlideOutContent,
  CareHoursStartSlideOutContent,
} from '@/components';
import { MethodologyCallout } from '@/shared/components/methodology-callout';
import {
  AnimationToggle,
  OnboardingLayout,
  CareIntensityAnimation,
  useSlideSetup,
  useAnimationPreferences,
} from '@/features/client-onboarding';

import { useSelector } from 'react-redux';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';

import { startCase } from 'lodash';
import { possessivePronoun } from '@/util';
import { selectClient, selectIntakeSurvey } from '@/model';
import {
  possessive,
  carePhaseDefs,
  formatDurationYears,
  CarePhase,
  isDurationCustom,
} from '@shared';

const WEEKS_PER_MONTH = 4.33;

function phaseHoursPerWeek({
  phaseCareHoursNeeded,
  phaseCareMonthsNeeded,
}: {
  phaseCareHoursNeeded: number;
  phaseCareMonthsNeeded: number;
}) {
  if (!phaseCareMonthsNeeded) return 0;

  return phaseCareHoursNeeded / phaseCareMonthsNeeded / WEEKS_PER_MONTH;
}

export function OnboardingPredictedCareNeeds(_props: ClientIdProps) {
  const client = useSelector(selectClient);
  const {
    clientFirstName,
    phaseCosts,
    allPhaseCosts: { allPhaseCareMonthsNeeded },
  } = client;
  const intakeSurvey = useSelector(selectIntakeSurvey);
  const { animationsEnabled } = useAnimationPreferences();
  const { flags } = useFeatureFlags();
  const showAnimations = flags.animatedSlides && animationsEnabled;

  const lengthOfCareYears = allPhaseCareMonthsNeeded / 12;

  const earlyPhaseIndex = carePhaseDefs[CarePhase.earlyCare].index;
  const earlyPhaseCosts = phaseCosts[earlyPhaseIndex];
  const initialHoursPerWeek = phaseHoursPerWeek(earlyPhaseCosts);

  const latePhaseIndex = carePhaseDefs[CarePhase.fullCare].index;
  const latePhaseCosts = phaseCosts[latePhaseIndex];
  const laterHoursPerWeek = phaseHoursPerWeek(latePhaseCosts);

  useSlideSetup({
    slidePage: OnboardingPredictedCareNeeds,
    nextButtonLabel: `See ${possessive(clientFirstName)} Costs`,
  });

  return (
    <OnboardingLayout>
      <div className="flex h-max flex-col items-center justify-center pb-4">
        <div className="w-full py-2 md:py-8">
          <div className="mb-6 flex w-full grow items-center justify-between gap-6">
            <h1 className="text-balance text-2xl font-bold text-gray-950 md:text-4xl">
              {possessive(clientFirstName)} predicted care needs
            </h1>
            {flags.animatedSlides && (
              <div className="flex items-center gap-2">
                <AnimationToggle />
              </div>
            )}
          </div>
          {showAnimations ? (
            <CareIntensityAnimation
              clientFirstName={clientFirstName}
              lengthOfCareYears={lengthOfCareYears}
              initialHoursPerWeek={initialHoursPerWeek}
              laterHoursPerWeek={laterHoursPerWeek}
              intakeSurvey={intakeSurvey}
              isDurationEdited={isDurationCustom(phaseCosts)}
            />
          ) : (
            <div className="flex w-full grow flex-col gap-4 sm:flex-row">
              <ColumnLengthOfCareYears />
              <ColumnCareHoursLtcStart />
              <ColumnCareHoursLtcEnd />
            </div>
          )}
        </div>
        <div className="mt-auto" />
      </div>
    </OnboardingLayout>
  );
}

export function ColumnLengthOfCareYears() {
  const intakeSurvey = useSelector(selectIntakeSurvey);
  const {
    phaseCosts,
    allPhaseCosts: { allPhaseCareMonthsNeeded },
  } = useSelector(selectClient);
  const ltcDurationYears = allPhaseCareMonthsNeeded / 12;
  return (
    <div
      className="border-primary-200 flex grow basis-1/2 flex-col justify-between rounded-2xl border p-4 text-center md:p-6"
      data-testid="predicted-care-needs-length"
    >
      <div
        className="text-foreground text-base font-medium md:text-xl"
        data-testid="predicted-duration-label"
      >
        {startCase(possessivePronoun(intakeSurvey))} length of care:{' '}
        <MethodologyCallout
          content={<DurationSlideOutContent />}
          title="AI"
          variant="icon"
          iconClassName="inline md:size-5"
        />
      </div>
      <div
        className="text-purple py-4 text-5xl font-semibold leading-normal md:text-9xl"
        data-testid="predicted-duration-value"
      >
        {formatDurationYears(ltcDurationYears)}
      </div>
      <div
        className="text-muted-foreground text-base font-normal md:text-xl"
        data-testid="predicted-duration-unit"
      >
        years
        {isDurationCustom(phaseCosts) && (
          <Badge
            variant="secondary"
            className="ml-1"
          >
            edited
          </Badge>
        )}
      </div>
    </div>
  );
}

function ColumnCareHoursLtcStart() {
  const intakeSurvey = useSelector(selectIntakeSurvey);
  const { phaseCosts } = useSelector(selectClient);
  const phaseIndex = carePhaseDefs[CarePhase.earlyCare].index;
  const hoursPerWeek = phaseHoursPerWeek(phaseCosts[phaseIndex]);

  return (
    <div
      className="border-primary-200 flex grow basis-1/2 flex-col justify-between rounded-2xl border p-4 text-center md:p-6"
      data-testid="predicted-care-needs-initial-hours"
    >
      <div
        className="text-foreground text-base font-medium md:text-xl"
        data-testid="predicted-initial-hours-label"
      >
        {startCase(possessivePronoun(intakeSurvey))} initial care needs:{' '}
        <MethodologyCallout
          content={<CareHoursStartSlideOutContent />}
          title="AI"
          variant="icon"
          iconClassName="inline md:size-5"
        />
      </div>
      <div
        className="text-purple py-4 text-5xl font-semibold leading-normal md:text-9xl"
        data-testid="predicted-initial-hours-value"
      >
        {Math.round(hoursPerWeek)}
      </div>
      <div
        className="text-muted-foreground text-base font-normal md:text-xl"
        data-testid="predicted-initial-hours-unit"
      >
        hours/week
      </div>
    </div>
  );
}

function ColumnCareHoursLtcEnd() {
  const { phaseCosts } = useSelector(selectClient);
  const intakeSurvey = useSelector(selectIntakeSurvey);
  const phaseIndex = carePhaseDefs[CarePhase.fullCare].index;
  const hoursPerWeek = phaseHoursPerWeek(phaseCosts[phaseIndex]);

  return (
    <div
      className="border-primary-200 flex grow basis-1/2 flex-col justify-between rounded-2xl border p-4 text-center md:p-6"
      data-testid="predicted-care-needs-later-hours"
    >
      <div
        className="text-foreground text-base font-medium md:text-xl"
        data-testid="predicted-later-hours-label"
      >
        {startCase(possessivePronoun(intakeSurvey))} later care needs:{' '}
        <MethodologyCallout
          content={<CareHoursEndSlideOutContent />}
          title="AI"
          variant="icon"
          iconClassName="inline md:size-5"
        />
      </div>
      <div
        className="text-purple py-4 text-5xl font-semibold leading-normal md:text-9xl"
        data-testid="predicted-later-hours-value"
      >
        {Math.round(hoursPerWeek)}
      </div>
      <div
        className="text-muted-foreground text-base font-normal md:text-xl"
        data-testid="predicted-later-hours-unit"
      >
        hours/week
      </div>
    </div>
  );
}
